import React, { useMemo } from 'react';
import styles from './M11Hero.module.scss';
import { ModuleContainer } from '../ModuleContainer';
import { M11HeroModule } from '~/lib';
import clsx from 'clsx';
import { Container } from '~/shared/components/Container';
import { HeroContent } from '~/shared/components/HeroContent/HeroContent';
import { HeroImage } from '~/shared/components/HeroImage/HeroImage';
import { Watermark } from '~/shared/components/Watermarks';
import { defaultColorTheme } from '~/shared/utils/defaultColorTheme';
import { extractVideoSource } from '~/shared/components/Video/get-video-source';
export interface CustomCSSProperties extends React.CSSProperties {
    [key: `--${string}`]: string | number;
}

export const M11Hero: React.FC<M11HeroModule> = ({
    trumpet,
    title,
    paragraphText,
    link,
    image,
    imageAltText,
    colorTheme,
    imageOnBackground,
    watermark,
    spacingBottom,
    spacingTop,
    disclaimer,
    titleSize,
    video,
    reverseContent,
    aboveTheFold,
    ...rest
}) => {
    const {
        mainColor,
        highlightColor,
        foregroundColor,
        secondaryColor,
        secondaryColorOpacity,
        gradientHighlight,
        textColor,
    } = colorTheme || defaultColorTheme;

    const watermarkElement = useMemo(() => {
        if (!watermark) return null;

        return <Watermark type={watermark as 'Logo' | 'Percentage'} />;
    }, [watermark]);

    const videoSource = extractVideoSource(video, rest);

    const noMedia = !image?.src && !videoSource;

    const memoizedCustomProperties = useMemo<CustomCSSProperties>(
        () => ({
            '--color-main': mainColor,
            '--color-foreground': foregroundColor,
            '--color-watermark-bg': secondaryColor,
            '--color-watermark-opacity': secondaryColorOpacity,
            '--color-image-shadow': highlightColor,
            '--color-text': textColor,
            '--color-bullet-color': mainColor === '#f68a20' ? foregroundColor : '#f68a20',
        }),
        [],
    );

    return (
        <ModuleContainer
            hasGutter={!aboveTheFold}
            spacingTop={aboveTheFold ? 'none' : spacingTop}
            {...rest}
            style={memoizedCustomProperties}
        >
            <Container>
                <div
                    className={clsx(styles.wrapperHero, {
                        [styles.noImage]: noMedia,
                    })}
                >
                    <div
                        className={clsx(styles.background, {
                            [styles.noImage]: noMedia,
                        })}
                    >
                        {!imageOnBackground && watermark && (
                            <div
                                className={clsx(styles.watermark, {
                                    [styles.watermarkLogo]: watermark === 'Logo',
                                    [styles.watermarkPercent]: watermark == 'Percentage',
                                    [styles.waterMarkNoImg]: noMedia,
                                })}
                            >
                                {watermarkElement}
                            </div>
                        )}
                    </div>
                    <div
                        className={clsx(styles.heroContainer, {
                            [styles.noImage]: noMedia,
                            [styles.reversed]: reverseContent,
                        })}
                    >
                        <HeroContent
                            customProperties={memoizedCustomProperties}
                            noMedia={noMedia}
                            trumpet={trumpet}
                            title={title}
                            titleSize={titleSize}
                            paragraphText={paragraphText}
                            link={link}
                            foregroundColor={foregroundColor}
                        />

                        {(image?.src || videoSource) && (
                            <HeroImage
                                {...rest}
                                customProperties={memoizedCustomProperties}
                                image={image}
                                imageAltText={imageAltText}
                                watermark={watermark}
                                imageOnBackground={imageOnBackground}
                                video={videoSource}
                                title={title}
                                cta={link}
                                watermarkElement={watermarkElement}
                                gradientHighlight={gradientHighlight}
                                priority={aboveTheFold}
                            />
                        )}
                    </div>

                    {disclaimer && (
                        <div
                            className={clsx(styles.disclaimer, {
                                [styles.noImage]: noMedia,
                            })}
                            dangerouslySetInnerHTML={{ __html: disclaimer }}
                        />
                    )}
                </div>
            </Container>
        </ModuleContainer>
    );
};
